/**
 * @file psd/renderer.ts
 * @description Renders parsed PSD objects into HCIE LayerClass objects.
 */

import { LayerClass } from '@hcie/core';
import { PSDNode, PSDInstance, AgPsdData } from './types';
import { PSD_BLEND_MAP, getCleanName } from './utils';

export interface PsdRenderResult {
  width: number;
  height: number;
  layers: LayerClass[];
}

async function processNode(node: PSDNode, width: number, height: number): Promise<LayerClass[]> {
  if (node.isGroup()) {
    const results = await Promise.all(
      [...node.children()].reverse().map(child => processNode(child, width, height))
    );
    return results.flat();
  }

  const name = getCleanName(node);
  const layer = new LayerClass(name, width, height);
  layer.visible = node.visible();

  if (node.layer?.opacity != null) {
    layer.opacity = node.layer.opacity / 255;
  }
  const blendKey = node.layer?.blendMode?.blendKey;
  if (blendKey && PSD_BLEND_MAP[blendKey]) {
    layer.blendMode = PSD_BLEND_MAP[blendKey];
  }

  try {
    const img = node.toPng();
    if (img instanceof HTMLImageElement) {
      await new Promise<void>((resolve) => {
        if (img.complete && img.naturalWidth > 0) { resolve(); return; }
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
      (layer.ctx as CanvasRenderingContext2D).drawImage(img, node.left, node.top);
    } else if (node.layer?.image?.pixelData) {
      const { width: lw, height: lh, left, top } = node;
      if (lw > 0 && lh > 0) {
        const imageData = new ImageData(new Uint8ClampedArray(node.layer.image.pixelData), lw, lh);
        (layer.ctx as CanvasRenderingContext2D).putImageData(imageData, left, top);
      }
    }
  } catch (err) {
    console.error('Error drawing layer image:', err);
  }

  return [layer];
}

export async function convertPsdToLayers(psdObj: PSDInstance): Promise<PsdRenderResult> {
  if (!psdObj) return { width: 0, height: 0, layers: [] };

  // Handle ag-psd fallback
  if (psdObj.__isAgPsd && psdObj.data) {
    const data: AgPsdData = psdObj.data;
    const width = data.width || 0;
    const height = data.height || 0;
    const results: LayerClass[] = [];

    if (data.children) {
        for (const child of [...data.children].reverse()) {
            if (child.canvas) {
                const layer = new LayerClass(child.name || 'Layer', width, height);
                layer.visible = !child.hidden;
                layer.opacity = child.opacity != null ? child.opacity : 1;
                layer.blendMode = 'source-over'; 
                
                const ctx = (layer.ctx as CanvasRenderingContext2D);
                ctx.drawImage(child.canvas as any, child.left || 0, child.top || 0);
                results.push(layer);
            }
        }
    }
    return { width, height, layers: results };
  }

  // Original PSD.js logic
  const tree = psdObj.tree();
  const width = tree.width;
  const height = tree.height;
  const children = tree.children();
  
  const layerLists = await Promise.all(
    [...children].reverse().map(node => processNode(node, width, height))
  );
  
  return { width, height, layers: layerLists.flat() };
}
